import { useMutation } from '@tanstack/react-query'
import { useCopyToClipboard } from '@uidotdev/usehooks'
import svgToTinyDataUri from 'mini-svg-data-uri'
import { useQueryState } from 'nuqs'
import posthog from 'posthog-js'
import { memo, useDeferredValue, useEffect, useState } from 'react'
import { Button, parseColor, type Color } from 'react-aria-components'
import { trpc } from '#app/lib/trpc-tanstack.tsx'
import { cx } from '#app/utils/cx'
import { generateComponent } from '#app/utils/component-generators'
import { getCustomizedSVG } from '#app/utils/misc.js'
import { type IconFormat, searchParamsParsers } from '#app/utils/search-params'
import { useOptionalUser } from '#app/utils/user.js'

export function IconCard({
	icon,
	format,
	showIconName = true,
}: {
	icon: {
		id: string
		name: string
		svg: string
		color: string | null // snapshot hex color (overrides search params)
		strokeWidth: number | null // snapshot strokeWidth (overrides search params)
		originalIconId?: string | null // for recently used icons
	}
	format: IconFormat
	showIconName?: boolean
}) {
	const [urlColor] = useQueryState('color', searchParamsParsers.color)
	const [urlStrokeWidth] = useQueryState(
		'strokeWidth',
		searchParamsParsers.strokeWidth,
	)
	const [query] = useQueryState('query', searchParamsParsers.query)
	const [searchType] = useQueryState(
		'searchType',
		searchParamsParsers.searchType,
	)

	// Use snapshot values if provided, otherwise use URL params
	const color = icon.color ? parseColor(icon.color) : urlColor
	const strokeWidth = icon.strokeWidth ?? urlStrokeWidth

	const deferredColor = useDeferredValue(color)
	const deferredStrokeWidth = useDeferredValue(strokeWidth)

	const user = useOptionalUser()

	const recordRecentlyUsedMutation = useMutation({
		...trpc.recentlyUsed.record.mutationOptions(),
	})

	const handleAction = () => {
		posthog.capture('icon_action', {
			iconName: icon.name,
			searchQuery: query || null,
			searchType: searchType || null,
			format,
		})

		// Track recently used (fire-and-forget for logged in users)
		if (user) {
			recordRecentlyUsedMutation.mutate({
				svg: icon.svg,
				color: deferredColor.toString('hex'),
				strokeWidth: deferredStrokeWidth,
				iconName: icon.name,
				format,
				originalIconId: icon.originalIconId ?? undefined,
			})
		}
	}

	return (
		<IconCardInner
			icon={icon}
			color={deferredColor}
			strokeWidth={deferredStrokeWidth}
			format={format}
			onAction={handleAction}
			showIconName={showIconName}
		/>
	)
}

function downloadSvg(svg: string, name: string) {
	const blob = new Blob([svg], { type: 'image/svg+xml' })
	const url = URL.createObjectURL(blob)
	const a = document.createElement('a')
	a.href = url
	a.download = `${name}.svg`
	document.body.appendChild(a)
	a.click()
	document.body.removeChild(a)
	URL.revokeObjectURL(url)
}

const formatLabels: Record<IconFormat, { idle: string; done: string }> = {
	notion: { idle: 'Copy Notion link', done: 'Copied!' },
	'download-svg': { idle: 'Download SVG', done: 'Downloaded!' },
	'copy-svg': { idle: 'Copy SVG', done: 'Copied!' },
	'copy-react': { idle: 'Copy React', done: 'Copied!' },
	'copy-vue': { idle: 'Copy Vue', done: 'Copied!' },
	'copy-svelte': { idle: 'Copy Svelte', done: 'Copied!' },
	'copy-solid': { idle: 'Copy Solid', done: 'Copied!' },
}

const IconCardInner = memo(function IconCardInner({
	icon,
	color,
	strokeWidth,
	format,
	onAction,
	showIconName,
}: {
	icon: { name: string; svg: string }
	color: Color
	strokeWidth: number
	format: IconFormat
	onAction: () => void
	showIconName: boolean
}) {
	const customized = getCustomizedSVG(
		icon.svg,
		color.toString('hex'),
		strokeWidth,
	)

	const [, copyToClipboard] = useCopyToClipboard()
	const [state, setState] = useState<'idle' | 'active' | 'done'>('idle')

	useEffect(() => {
		if (state === 'done') {
			const handle = window.setTimeout(() => {
				setState('idle')
			}, 1000)
			return () => {
				window.clearTimeout(handle)
			}
		}
	}, [state])

	const handlePress = () => {
		if (state === 'done') return

		switch (format) {
			case 'notion': {
				const svgDataUri = svgToTinyDataUri(customized.svg)
				void copyToClipboard(svgDataUri)
				break
			}
			case 'copy-svg':
				void copyToClipboard(customized.svg)
				break
			case 'download-svg':
				downloadSvg(customized.svg, icon.name)
				break
			case 'copy-react':
			case 'copy-vue':
			case 'copy-svelte':
			case 'copy-solid': {
				// Strip width="100%" height="100%" that getCustomizedSVG adds for web display
				const cleanSvg = customized.svg
					.replace(/ width="100%"/, '')
					.replace(/ height="100%"/, '')
				const framework = format.replace('copy-', '') as
					| 'react'
					| 'vue'
					| 'svelte'
					| 'solid'
				const component = generateComponent(cleanSvg, icon.name, framework)
				void copyToClipboard(component)
				break
			}
		}

		setState('done')
		onAction()
	}

	return (
		<Button
			className="flex w-full cursor-pointer flex-col gap-2 focus:outline-none"
			type="button"
			onHoverStart={() => {
				if (state === 'idle') {
					setState('active')
				}
			}}
			onHoverEnd={() => {
				if (state === 'active') {
					setState('idle')
				}
			}}
			onPress={handlePress}
		>
			{({ isFocusVisible, isHovered }) => (
				<>
					<div className="relative w-full" aria-hidden="true">
						<div
							className={cx(
								'bg-primary_alt outline-focus-ring border-secondary flex aspect-square items-center justify-center rounded-xl border transition duration-100 ease-linear',
								isHovered ? 'bg-primary_hover' : '',
								isFocusVisible ? 'outline-2 outline-offset-2' : '',
							)}
						>
							<div
								className="size-9"
								dangerouslySetInnerHTML={{ __html: customized.svg }}
							/>
						</div>
						<div
							className={cx(
								'absolute right-1.5 bottom-1.5 left-1.5 flex flex-col gap-1 transition duration-200 ease-linear',
								state === 'done' || isHovered || isFocusVisible
									? 'translate-y-0 opacity-100'
									: 'translate-y-1 opacity-0',
							)}
						>
							<span className="bg-primary text-secondary ring-border-primary flex justify-center rounded-md py-1 text-center text-xs font-semibold shadow-xs ring-1 transition duration-100 ease-linear ring-inset">
								{state === 'done'
									? formatLabels[format].done
									: formatLabels[format].idle}
							</span>
						</div>
					</div>
					{showIconName && (
						<div
							className="text-primary truncate text-xs font-semibold"
							title={icon.name}
						>
							<span className="sr-only">{formatLabels[format].idle} </span>
							{icon.name}
						</div>
					)}
				</>
			)}
		</Button>
	)
})
